import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { ReportOutlined, CheckCircleOutline, InfoOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

export const statusCodes = {
  OK: 'OK',
  ERROR: 'ERROR',
  WARNING: 'WARNING',
  UNKNOWN: 'UNKNOWN'
};

const StatusIndicator = ({ paramCode, status }) => {
  const [info, setInfo] = useState();
  const [t] = useTranslation();
  const isSuccessful = status.code === statusCodes.OK;
  const isError = status.code === statusCodes.ERROR;
  const isWarning = status.code === statusCodes.WARNING;
  const iconColor = isSuccessful ? 'success' : isError ? 'error' : isWarning ? 'warning' : 'default';
  const label = isSuccessful ? 'General.Valid' : isError ? 'General.Error' : isWarning ? 'General.Warning' : 'General.Unknown';

  const handleInformationClose = useCallback(() => setInfo(undefined), []);
  const icon = isSuccessful ? (
    <CheckCircleOutline color={iconColor} />
  ) : isError || isWarning ? (
    <ReportOutlined color={iconColor} />
  ) : (
    <InfoOutlined color={iconColor} />
  );

  const handleStatusClick = useCallback(() => {
    if (isSuccessful) {
      setInfo(`The parameter '${paramCode}' is correctly configured.`);
      return;
    }
    if (status.errorMessage) {
      setInfo(`'${paramCode}': ${status.errorMessage}`);
      return;
    }
    setInfo(`The parameter '${paramCode}' evaluation could not be completed.`);
  }, [paramCode, isSuccessful, status.errorMessage]);

  return (
    <>
      <IconButton id='show-status' size='small' variant='text' tooltip={t(label)} onClick={handleStatusClick} disabled={false}>
        {icon}
      </IconButton>
      {info && <InformationDialog open={!!info} onClose={handleInformationClose} icon={icon} title={t(label)} message={info} />}
    </>
  );
};

StatusIndicator.propTypes = {
  paramCode: PropTypes.string.isRequired,
  status: PropTypes.shape({
    code: PropTypes.string.isRequired,
    errorMessage: PropTypes.string
  }).isRequired
};

const InformationDialog = ({ open, onClose, icon, title, message }) => {
  const [t] = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby='dialog-title' aria-describedby='dialog-description'>
      {title && (
        <DialogTitle id='dialog-title'>
          <Box display='flex' alignItems='center'>
            {icon}
            <Box ml={1}>{title}</Box>
          </Box>
        </DialogTitle>
      )}
      <DialogContent>
        <DialogContentText id='dialog-description'>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={onClose} autoFocus>
          {t('General.Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

InformationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  icon: PropTypes.node,
  title: PropTypes.string,
  message: PropTypes.string
};

export default StatusIndicator;
